import { Box, Group, Modal, Stack, Input, Overlay, Center } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconLock, IconSignatureOff, IconUserPlus } from '@tabler/icons-react';
import Button from 'components/Button';
import PredefinedDataAutosuggestInput from 'components/inputs/predefined-data-autosuggest-input/PredefinedDataAutosuggestInput';
import P1Regular from 'components/typography/P1Regular';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export interface ConfirmationFormData {
  name: string;
  signature: string; // Base64 encoded image
  date?: string;
  time?: string;
}

type OnConfirmCallback = (confirmation: ConfirmationFormData) => void;

interface GatherConfirmationParams {
  title?: string;
  initialValues?: ConfirmationFormData;
  autosuggestDataType?: string;
  onConfirm: OnConfirmCallback;
}

interface GatherConfirmationModalProps extends GatherConfirmationParams {
  opened: boolean;
  onClose: () => void;
}

type GatherConfirmation = (params: GatherConfirmationParams) => void;

interface IGatherConfirmationContext {
  gatherConfirmation: GatherConfirmation;
}

const GatherConfirmationContext = createContext<IGatherConfirmationContext>(undefined!);

export const CONFIRMATION_ROW_INITIAL_VALUE: ConfirmationFormData = {
  name: '',
  signature: '',
};

/**
 * Modal for gathering confirmation.
 */
function GatherConfirmationModal({
  opened,
  onClose,
  title = 'Pridať potvrdenie',
  initialValues = CONFIRMATION_ROW_INITIAL_VALUE,
  autosuggestDataType,
  onConfirm,
}: GatherConfirmationModalProps) {
  const signature = useRef<SignatureCanvas>(null);
  const [nameIsFixed, setNameIsFixed] = useState(false);

  const form = useForm<ConfirmationFormData>({
    initialValues: initialValues,
    validate: {
      name: (value) => (value.trim().length === 0 ? 'Meno je povinné' : null),
      signature: () => (signature.current?.isEmpty() ? 'Digitálny podpis je povinný' : null),
    },
  });

  const handleSubmit = useCallback(
    (values: ConfirmationFormData) => {
      onConfirm(values);
      onClose();
    },
    [onConfirm, onClose]
  );

  const resetCanvas = useCallback(() => {
    if (signature.current) {
      signature.current.clear();
      form.setFieldValue('signature', '');
      form.setFieldValue('date', undefined);
      form.setFieldValue('time', undefined);
    }
  }, []);

  const fixName = useCallback(() => setNameIsFixed(true), []);

  const unfixName = useCallback(() => {
    setNameIsFixed(false);
    resetCanvas();
  }, [resetCanvas]);

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="auto" centered zIndex={300}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing={24}>
          <Group spacing={8} align="end" noWrap>
            <PredefinedDataAutosuggestInput
              w="100%"
              withAsterisk
              readOnly={nameIsFixed}
              label="Potvrdil"
              placeholder="Meno a priezvisko dolu podpísaného"
              predefinedDataTypeId={autosuggestDataType ? Number(autosuggestDataType) : undefined}
              {...form.getInputProps('name')}
            />
            <Button
              variant="subtle"
              size="sm"
              onClick={nameIsFixed ? unfixName : fixName}
              leftIcon={nameIsFixed ? <IconEdit /> : <IconUserPlus />}
            >
              {nameIsFixed ? 'Zmeniť' : 'Nastaviť'}
            </Button>
          </Group>
          <Input.Wrapper withAsterisk label="Digitálny podpis">
            <Box
              w={500}
              h={200}
              pos="relative"
              sx={{
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: form.errors.signature ? 'var(--mantine-color-red-8)' : 'var(--mantine-color-gray-4)',
                borderRadius: 4,
                overflow: 'hidden',
                pointerEvents: nameIsFixed ? 'auto' : 'none',
              }}
            >
              {!nameIsFixed && (
                <Overlay opacity={0.08}>
                  <Center h="100%" pb={16}>
                    <Stack spacing={4}>
                      <Box component={IconLock} mx="auto" />
                      <P1Regular>Najprv nastavte meno potvrdzujúceho</P1Regular>
                    </Stack>
                  </Center>
                </Overlay>
              )}
              <SignatureCanvas
                ref={signature}
                penColor="#284283"
                canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                onEnd={() => {
                  if (signature.current) {
                    form.setFieldValue('signature', signature.current?.toDataURL() || '');
                    form.setFieldValue('date', new Date().toLocaleDateString('sk-SK'));
                    form.setFieldValue('time', new Date().toLocaleTimeString('sk-SK'));
                  }
                }}
              />
            </Box>
            <Input.Error mt={4}>{form.errors.signature}</Input.Error>
          </Input.Wrapper>
          <Group position="apart">
            <Group position="left">
              {nameIsFixed && (
                <Button variant="subtle-gray" onClick={resetCanvas} leftIcon={<IconSignatureOff />}>
                  Resetovať podpis
                </Button>
              )}
            </Group>
            <Group position="right">
              <Button variant="subtle-gray" onClick={onClose}>
                Späť
              </Button>
              <Button variant="primary" type="submit">
                Pridať potvrdenie
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Provider for gathering confirmation.
 */
export function GatherConfirmationProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<GatherConfirmationParams | undefined>(undefined);

  /**
   * Opens the modal for gathering confirmation.
   */
  const gatherConfirmation: GatherConfirmation = useCallback((params) => {
    setParams(params);
    open();
  }, []);

  const value = useMemo(() => ({ gatherConfirmation }), [gatherConfirmation]);

  return (
    <GatherConfirmationContext.Provider value={value}>
      {children}
      {opened && <GatherConfirmationModal opened onClose={close} {...params!} />}
    </GatherConfirmationContext.Provider>
  );
}

/**
 * Hook to use the gather confirmation context.
 */
export function useGatherConfirmation() {
  return useContext(GatherConfirmationContext);
}
